import React, { useEffect, useMemo, useState } from "react";
import useOnboardingStore from "../useOnboardingStore";
import {
  IDENTIFIER_TYPE,
  OTP_LENGTH,
  validateIdentifier,
} from "utils/constants/onboarding";
import styles from "../Form.module.scss";

function ContactVerifyInput({ substep, value, onChange, error, isReadOnly, channel }) {
  const requestOtp = useOnboardingStore((s) => s.requestOtp);
  const verifySecondChannel = useOnboardingStore((s) => s.verifySecondChannel);
  const identifier = useOnboardingStore((s) => s.identifier);
  const identifierType = identifier?.type ?? "";
  const identifierValue = identifier?.value ?? "";

  const isSignupChannel =
    (channel === "phone" && identifierType === IDENTIFIER_TYPE.PHONE) ||
    (channel === "email" && identifierType === IDENTIFIER_TYPE.EMAIL);

  const stored = value?.string_value ?? "";
  // `stored` in deps: re-fires after markStepComplete → fetchFlow wipes editedValues,
  // preventing the signup-channel value from silently vanishing between steps.
  useEffect(() => {
    if (isSignupChannel && !stored && identifierValue) {
      onChange(substep.substep_id, { string_value: identifierValue, verified: true });
    }
    
  }, [isSignupChannel, identifierValue, stored]);

  // Guard: only show locked badge when we have a displayable value — empty + "✓ Verified" is a UI lie.
  const displayValue = stored || identifierValue;
  if (isSignupChannel && displayValue) {
    return (
      <div className={styles.fieldBody}>
        <div className={styles.verifiedRow}>
          <input
            className={styles.input}
            type="text"
            value={displayValue}
            disabled
            readOnly
          />
          <span className={styles.verifiedBadge}>Verified</span>
        </div>
      </div>
    );
  }

  return (
    <SecondChannelVerify
      substep={substep}
      value={value}
      onChange={onChange}
      error={error}
      isReadOnly={isReadOnly}
      channel={channel}
      requestOtp={requestOtp}
      verifySecondChannel={verifySecondChannel}
    />
  );
}

function SecondChannelVerify({
  substep,
  value,
  onChange,
  error,
  isReadOnly,
  channel,
  requestOtp,
  verifySecondChannel,
}) {
  const draftValue = value?.string_value ?? "";
  const isVerified = !!value?.verified;
  const channelType = channel === "phone" ? IDENTIFIER_TYPE.PHONE : IDENTIFIER_TYPE.EMAIL;

  const [stage, setStage] = useState(isVerified ? "verified" : "input");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [timer, setTimer] = useState(0);

  useEffect(() => {
    if (timer <= 0) return undefined;
    const id = setTimeout(() => setTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [timer]);

  const validationError = useMemo(
    () => (draftValue ? validateIdentifier(channelType, draftValue) : null),
    [draftValue, channelType]
  );

  const onValueChange = (e) => {
    const next = channel === "phone" ? e.target.value.replace(/\D/g, "") : e.target.value;
    onChange(substep.substep_id, { string_value: next, verified: false });
    if (stage === "otp") {
      setStage("input");
      setOtp("");
    }
  };

  const handleSend = async () => {
    setLocalError(null);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    setBusy(true);
    const res = await requestOtp(channelType, draftValue);
    setBusy(false);
    if (res.ok) {
      setStage("otp");
      setTimer(30);
    } else {
      setLocalError(res.message);
    }
  };

  const handleVerify = async () => {
    setLocalError(null);
    if (otp.length !== OTP_LENGTH) {
      setLocalError(`Enter the ${OTP_LENGTH}-digit code`);
      return;
    }
    setBusy(true);
    const res = await verifySecondChannel({
      substepId: substep.substep_id,
      identifierValue: draftValue,
      otp,
    });
    setBusy(false);
    if (res.ok) {
      onChange(substep.substep_id, { string_value: draftValue, verified: true });
      setStage("verified");
    } else {
      setLocalError(res.message || "Could not verify. Try again.");
    }
  };

  const showError = error || localError;

  if (stage === "verified") {
    return (
      <div className={styles.fieldBody}>
        <div className={styles.verifiedRow}>
          <input className={styles.input} type="text" value={draftValue} disabled readOnly />
          <span className={styles.verifiedBadge}>Verified</span>
        </div>
      </div>
    );
  }

  const cfg = substep?.input_text ?? {};

  return (
    <div className={styles.fieldBody}>
      <div className={styles.verifyRow}>
        <input
          className={`${styles.input} ${showError ? styles.inputError : ""}`}
          type={channel === "email" ? "email" : "tel"}
          value={draftValue}
          placeholder={cfg.placeholder || (channel === "phone" ? "10-digit phone" : "name@company.com")}
          maxLength={channel === "phone" ? 10 : cfg.max_length || 200}
          disabled={isReadOnly || busy}
          onChange={onValueChange}
        />
        <button
          type="button"
          className={styles.secondaryBtn}
          onClick={handleSend}
          disabled={busy || isReadOnly || !draftValue || !!validationError || (stage === "otp" && timer > 0)}
        >
          {stage === "otp"
            ? timer > 0
              ? `Resend in ${timer}s`
              : "Resend OTP"
            : busy
              ? "Sending…"
              : "Send OTP"}
        </button>
      </div>

      {stage === "otp" && (
        <div className={styles.verifyOtpRow}>
          <input
            className={`${styles.input} ${styles.otpInput}`}
            type="text"
            inputMode="numeric"
            value={otp}
            placeholder={`${OTP_LENGTH}-digit code`}
            maxLength={OTP_LENGTH}
            disabled={busy}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
          />
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={handleVerify}
            disabled={busy || otp.length !== OTP_LENGTH}
          >
            {busy ? "Verifying…" : "Verify"}
          </button>
        </div>
      )}

      {showError && <div className={styles.fieldError}>{showError}</div>}
    </div>
  );
}

export default ContactVerifyInput;
